import { PropsWithChildren, useEffect, useState } from "react";

import { EventBusTopic } from "@moodys/mdc-frontend.utils.event-bus";
import {
  Backdrop,
  Box,
  ScopedCssBaseline,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { analytics, MoodysLogo, split } from "@moodys/federated";
import { isJapaneseLanguageSetPage } from "components/Header/components/HeaderWithContext/headerWithContext.helpers";
import { useHeaderContext } from "components/Header/context";
import { ScrollLockItem, useLayoutContext } from "components/Layout";
import { useMenu } from "components/Menu";
import { Menu } from "components/Menu/Menu";
import { AccountMenu } from "components/Menu/components/AccountMenu";
import { ResearchAssistant } from "components/ResearchAssistant/ResearchAssistant";
import { TypeaheadSearch } from "components/TypeaheadSearch/TypeaheadSearch";
import { config } from "config";
import { useEventBus } from "hooks/useEventBus";
import { SPLIT_ALERT_BANNER } from "services/split/split.constants";
import { generateHeaderAnalyticsProp } from "utils/generateAnalyticsTag";
import {
  getHomepageLinkForLanguage,
  getLanguageFromCookie,
  Language,
  setLanguageCookie,
} from "utils/language";

import { headerWithContextStyles as styles } from "./HeaderWithContext.styles";
import { AccessibeTriggerContainer } from "./components/AccessibeTriggerContainer/AccessibeTriggerContainer";
import { AlertBannerWrapper } from "./components/AlertBannerWrapper/AlertBannerWrapper";
import { FeedbackForm } from "./components/FeedbackForm/FeedbackForm";
import { GatewayDrawer } from "./components/GatewayDrawer/GatewayDrawer";

// eslint-disable-next-line complexity
export const HeaderWithContext = ({ children }: PropsWithChildren) => {
  const { addScrollLock, removeScrollLock } = useLayoutContext();
  const { menuOpen, setMenuOpen, setSubMenuClose, languageNodes } = useMenu();
  const [searchOpen, setSearchOpen] = useEventBus(EventBusTopic.SearchbarOpen);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const showSearchOnly = searchOpen && isTablet;
  const [authenticated] = useEventBus(EventBusTopic.IsAuthenticated);
  const homeLink = getHomepageLinkForLanguage(languageNodes);
  const isLightMode = useMediaQuery(
    "(forced-colors: active) and (prefers-color-scheme: light)"
  );
  const [color, setColor] = useState<"blue" | "white">(
    theme.palette.mode === "light" || isLightMode ? "blue" : "white"
  );
  const { isLegacy } = useHeaderContext();

  useEffect(() => {
    if (searchOpen) {
      addScrollLock(ScrollLockItem.SEARCH);
    } else {
      removeScrollLock(ScrollLockItem.SEARCH);
    }
  }, [searchOpen]);

  useEffect(() => {
    if (menuOpen && searchOpen) {
      setSearchOpen(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen && searchOpen) {
      setMenuOpen(false);
    }
  }, [searchOpen]);

  useEffect(() => {
    setColor(theme.palette.mode === "light" || isLightMode ? "blue" : "white");
  }, [isLightMode, theme.palette.mode]);

  useEffect(() => {
    if (
      getLanguageFromCookie() !== Language.JAPANESE &&
      isJapaneseLanguageSetPage(window.location.pathname, languageNodes)
    ) {
      setLanguageCookie(Language.JAPANESE);
    }
  }, [languageNodes]);

  return (
    <ScopedCssBaseline>
      <header
        css={styles.headerStyles(theme)}
        data-testid={config.HEADER_ID}
        data-js="mdc-shell-header"
        id={config.HEADER_ID}
      >
        <split.SplitWrapper
          splitName={SPLIT_ALERT_BANNER}
          treatmentElementMap={{
            on: <AlertBannerWrapper />,
          }}
        />
        <div css={[styles.headerContent]}>
          <div css={styles.leftContainer}>
            {!showSearchOnly && (
              <>
                <Menu />
                <a
                  href={homeLink}
                  css={styles.logoButton}
                  {...generateHeaderAnalyticsProp({
                    type: analytics.LinkType.IMAGE,
                    moduleName: "Home",
                    id: "Logo",
                    name: "Home Page Logo",
                  })}
                  aria-label="Moody's"
                  data-testid="header-home-button"
                >
                  <MoodysLogo
                    css={styles.logoMargin}
                    color={color}
                    height="14px"
                  />
                </a>
              </>
            )}
            <Box css={styles.searchRAContainer(theme)}>
              <TypeaheadSearch />
              {!searchOpen && <ResearchAssistant />}
            </Box>
          </div>
          {!showSearchOnly && (
            <div css={styles.rightContainer}>
              {isLegacy && <AccessibeTriggerContainer />}
              <AccountMenu />
              {authenticated && <GatewayDrawer />}
            </div>
          )}
        </div>
        <div>{children}</div>
      </header>
      {(searchOpen || menuOpen) && (
        <Backdrop
          open
          data-testid="header-backdrop"
          onClick={() => {
            if (searchOpen) {
              setSearchOpen(false);
            }

            if (menuOpen) {
              setSubMenuClose();
              setMenuOpen(false);
            }
          }}
        />
      )}
      <FeedbackForm />
    </ScopedCssBaseline>
  );
};
